import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { ensureCsrfToken, resetCsrfToken } from "../api/apiClient";
import * as authApi from "../api/authApi";
import { setAuthSessionHandlers } from "./authSessionHandler";
import {
  getPrimaryPartnerOrganization as getPrimaryPartnerOrganizationForUser,
  hasPartnerAccess as checkPartnerAccess,
} from "./partnerAccess";
import { setConsentRedirectHandler } from "./authConsentHandler";
import { isSuperAdminUser, normalizeRoleCode } from "./superAdmin";

export const AuthContext = createContext(null);

function normalizeUser(user) {
  if (!user) {
    return null;
  }

  const normalized = {
    ...user,
    permissions: Array.isArray(user.permissions) ? user.permissions : [],
    roles: Array.isArray(user.roles) ? user.roles : [],
    partnerOrganizations: Array.isArray(user.partnerOrganizations)
      ? user.partnerOrganizations
      : [],
    consent: user.consent || {
      requiresConsent: false,
      missingRequiredConsents: [],
    },
  };

  normalized.isSuperAdmin =
    normalized.isSuperAdmin === true || isSuperAdminUser(normalized);

  return normalized;
}

function checkPermissionList(user, permissionCodes, mode = "any") {
  if (!user) {
    return false;
  }

  if (isSuperAdminUser(user)) {
    return true;
  }

  const permissions = user.permissions || [];

  if (!permissionCodes?.length) {
    return true;
  }

  if (mode === "all") {
    return permissionCodes.every((code) => permissions.includes(code));
  }

  return permissionCodes.some((code) => permissions.includes(code));
}

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionMessage, setSessionMessage] = useState("");
  const authDebugLoggedKeyRef = useRef(null);

  const clearAuth = useCallback(() => {
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      setError(null);
      const result = await authApi.getCurrentUser();

      if (result?.success && result.user) {
        setUser(normalizeUser(result.user));
        return result.user;
      }

      setUser(null);
      return null;
    } catch (loadError) {
      setUser(null);
      setError(loadError.message || "Unable to verify your session.");
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setIsLoading(true);
      try {
        await ensureCsrfToken();
      } catch {
        // CSRF is required for mutating requests; login still works without it.
      }
      await refreshUser();
      if (!cancelled) {
        setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [refreshUser]);

  useEffect(() => {
    setConsentRedirectHandler((body) => {
      if (window.location.pathname === "/account/consent-required") {
        return;
      }

      navigate("/account/consent-required", {
        replace: true,
        state: {
          returnTo: window.location.pathname + window.location.search,
          missingRequiredConsents: body?.missingRequiredConsents,
        },
      });
    });

    return () => setConsentRedirectHandler(null);
  }, [navigate]);

  useEffect(() => {
    setAuthSessionHandlers({
      onUnauthorized: () => {
        const onLoginPage = window.location.pathname === "/login";
        clearAuth();

        if (!onLoginPage) {
          setSessionMessage("Your session has expired. Please sign in again.");
          const fromHash = window.location.hash || "";
          navigate("/login", {
            replace: true,
            state: { fromHash },
          });
        }
      },
      onForbidden: () => {
        navigate("/unauthorized", { replace: true });
      },
    });

    return () => setAuthSessionHandlers({});
  }, [clearAuth, navigate]);

  const login = useCallback(async (email, password) => {
    setError(null);
    setSessionMessage("");
    const result = await authApi.login(email, password);
    const nextUser = normalizeUser(result?.user);
    resetCsrfToken();
    await ensureCsrfToken();
    setUser(nextUser);
    return nextUser;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Clear local state even if the network call fails.
    }

    resetCsrfToken();
    clearAuth();
    setSessionMessage("");
    navigate("/login", { replace: true });
  }, [clearAuth, navigate]);

  const hasPermission = useCallback(
    (permissionCode) => checkPermissionList(user, [permissionCode], "any"),
    [user]
  );

  const hasAnyPermission = useCallback(
    (permissionCodes) => checkPermissionList(user, permissionCodes, "any"),
    [user]
  );

  const hasAllPermissions = useCallback(
    (permissionCodes) => checkPermissionList(user, permissionCodes, "all"),
    [user]
  );

  const hasRole = useCallback(
    (roleCode) =>
      isSuperAdminUser(user) ||
      (user?.roles?.some((role) => normalizeRoleCode(role) === roleCode) ?? false),
    [user]
  );

  const hasAnyRole = useCallback(
    (roleCodes) =>
      isSuperAdminUser(user) ||
      (roleCodes?.some((code) =>
        user?.roles?.some((role) => normalizeRoleCode(role) === code)
      ) ??
        false),
    [user]
  );

  const hasPartnerAccess = useCallback(
    () => checkPartnerAccess(user),
    [user]
  );

  const getPrimaryPartnerOrganization = useCallback(
    () => getPrimaryPartnerOrganizationForUser(user),
    [user]
  );

  const isSuperAdmin = isSuperAdminUser(user);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      isSuperAdmin,
      isLoading,
      error,
      sessionMessage,
      setSessionMessage,
      login,
      logout,
      refreshUser,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      hasRole,
      hasAnyRole,
      hasPartnerAccess,
      getPrimaryPartnerOrganization,
      clearAuth,
    }),
    [
      user,
      isSuperAdmin,
      isLoading,
      error,
      sessionMessage,
      login,
      logout,
      refreshUser,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      hasRole,
      hasAnyRole,
      hasPartnerAccess,
      getPrimaryPartnerOrganization,
      clearAuth,
    ]
  );

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") {
      return;
    }

    if (process.env.REACT_APP_AUTH_DEBUG !== "true") {
      return;
    }

    if (!user) {
      authDebugLoggedKeyRef.current = null;
      return;
    }

    const debugKey = user.id || user.email || "unknown";
    if (authDebugLoggedKeyRef.current === debugKey) {
      return;
    }

    authDebugLoggedKeyRef.current = debugKey;
    console.debug("[auth]", {
      email: user.email,
      roles: user.roles?.map((role) => normalizeRoleCode(role)),
      isSuperAdmin: isSuperAdminUser(user),
      permissionCount: user.permissions?.length ?? 0,
    });
  }, [user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}